package com.schoolconnect.app.feature.eventsnews

import com.schoolconnect.app.datasource.network.response.EventResponse
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.EventCreateRequest
import com.schoolconnect.app.datasource.network.request.EventUpdateRequest
import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.eventsnews.EventsNewsRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock

class EventsNewsViewModel(private val schoolId: String) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var listJob: Job? = null
    private var statisticsJob: Job? = null
    private var saveJob: Job? = null
    private var viewJob: Job? = null
    private val currentUserId = UserSessionStore.getUserId()
    private val _uiState = MutableStateFlow(EventsNewsUiState(schoolId = schoolId, isLoading = true))
    val uiState: StateFlow<EventsNewsUiState> = _uiState.asStateFlow()

    init { loadPage(); loadStatistics(); loadAudienceOptions(); loadClassTargetOptions() }

    private fun loadAudienceOptions() {
        scope.launch {
            SystemLookupRepository.loadCategoryItems("EVENT_AUDIENCE").onSuccess { items ->
                val options = items.mapNotNull { item ->
                    val code = item.code?.trim()?.takeIf { it.isNotBlank() }
                    val label = item.displayValue?.trim()?.takeIf { it.isNotBlank() }
                    if (code != null && label != null) EventAudienceOption(code, label) else null
                }
                _uiState.update { it.copy(audienceOptions = options) }
            }
        }
    }

    private fun loadClassTargetOptions() {
        if (schoolId.isBlank()) return
        scope.launch {
            ClassSectionRepository.loadSchoolClassSectionLookup(schoolId).onSuccess { sections ->
                val options = sections.mapNotNull { section ->
                    val id = section.id?.trim()?.takeIf { it.isNotBlank() }
                    val label = listOf(section.className?.trim().orEmpty(), section.sectionName?.trim().orEmpty())
                        .filter { it.isNotBlank() }.joinToString("-")
                    if (id != null && label.isNotBlank()) EventClassTargetOption(id, label) else null
                }.sortedWith { left, right -> compareEventClassLabels(left.label, right.label) }
                _uiState.update { it.copy(classTargetOptions = options) }
            }
        }
    }

    private fun compareEventClassLabels(left: String, right: String): Int {
        fun parts(value: String): Pair<Int?, String> {
            val classPart = value.substringBefore('-').trim()
            return classPart.toIntOrNull() to value
        }
        val (leftNumber, leftText) = parts(left)
        val (rightNumber, rightText) = parts(right)
        return when {
            leftNumber != null && rightNumber != null -> leftNumber.compareTo(rightNumber).takeIf { it != 0 }
                ?: leftText.compareTo(rightText, ignoreCase = true)
            leftNumber != null -> -1
            rightNumber != null -> 1
            else -> leftText.compareTo(rightText, ignoreCase = true)
        }
    }

    fun onEvent(event: EventsNewsUiEvent) {
        when (event) {
            EventsNewsUiEvent.RefreshClicked -> { loadPage(); loadStatistics() }
            is EventsNewsUiEvent.CreateSubmitted -> createEvent(event.draft)
            is EventsNewsUiEvent.UpdateSubmitted -> updateEvent(event.draft)
            is EventsNewsUiEvent.DeleteEvent -> deleteEvent(event.eventId)
            is EventsNewsUiEvent.ViewEvent -> loadEventDetail(event.eventId)
            is EventsNewsUiEvent.SearchChanged -> { _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }; loadPage(debounce = true) }
            is EventsNewsUiEvent.FiltersChanged -> { _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }; loadPage() }
            EventsNewsUiEvent.FiltersReset -> { _uiState.update { it.copy(filters = EventsNewsFilters(), searchQuery = "", pageIndex = 0) }; loadPage() }
            is EventsNewsUiEvent.PageChanged -> { _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }; loadPage() }
            is EventsNewsUiEvent.PageSizeChanged -> { _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; loadPage() }
            is EventsNewsUiEvent.SortChanged -> {
                _uiState.update {
                    val ascending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0)
                }
                loadPage()
            }
        }
    }

    private fun loadEventDetail(eventId: String) {
        val normalizedId = eventId.trim()
        if (normalizedId.isBlank()) return
        viewJob?.cancel()
        viewJob = scope.launch {
            _uiState.update { it.copy(viewingEvent = null, eventAttachments = emptyList(), isViewingEvent = true, viewEventError = "") }
            EventsNewsRepository.loadDetail(normalizedId).fold(
                onSuccess = { response ->
                    val fileIds = response.attachmentFileIds.orEmpty()
                    val fileUrls = response.attachmentFileUrls.orEmpty()
                    val attachments = (0 until maxOf(fileIds.size, fileUrls.size)).map { index ->
                        val fileId = fileIds.getOrNull(index).orEmpty()
                        val metadata = fileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getMetadata(it).getOrNull() }
                        val mimeType = metadata?.detectedMimeType ?: metadata?.declaredMimeType ?: "application/octet-stream"
                        val url = fileUrls.getOrNull(index).orEmpty().ifBlank {
                            fileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getDownloadUrl(it).getOrNull()?.url }.orEmpty()
                        }
                        EventAttachmentDetailUi(
                            fileId = fileId,
                            name = metadata?.originalFilename.orEmpty().ifBlank { "Attachment ${index + 1}" },
                            mimeType = mimeType,
                            sizeLabel = metadata?.sizeBytes?.let(::formatEventFileSize) ?: "File",
                            url = url,
                            previewBytes = if (mimeType.startsWith("image/", ignoreCase = true) && url.isNotBlank()) FileUploadRepository.downloadBytes(url).getOrNull() else null
                        )
                    }
                    _uiState.update { it.copy(viewingEvent = response, eventAttachments = attachments, isViewingEvent = false) }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] EventsNewsViewModel loadEventDetail failure eventId=$normalizedId", throwable)
                    _uiState.update { it.copy(isViewingEvent = false, viewEventError = throwable.message.orEmpty().ifBlank { "Unable to load event details." }) }
                }
            )
        }
    }

    private fun createEvent(draft: EventDraft) {
        val eventDate = parseDisplayDate(draft.date)
        val minimumEventDate = currentAppDateLabel()
        val validationError = when {
            schoolId.trim().isBlank() -> "A school context is required."
            draft.title.trim().isBlank() -> "Enter an event title."
            draft.description.trim().isBlank() -> "Enter event details."
            eventDate == null -> "Enter a valid date in DD MMM YYYY format."
            isEventDateBefore(draft.date, minimumEventDate) -> "Event date cannot be earlier than today."
            else -> null
        }
        if (validationError != null) {
            _uiState.update { it.copy(saveErrorMessage = validationError) }
            return
        }
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val audienceCode = resolveAudienceCode(draft.audience).getOrElse { throwable ->
                _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Event audience options are unavailable.") }
                return@launch
            }
            EventsNewsRepository.createEvent(
                EventCreateRequest(
                    schoolId = schoolId,
                    title = draft.title,
                    description = draft.description,
                    eventDate = "${eventDate}T23:59:59Z",
                    audience = audienceCode,
                    isActive = draft.isActive,
                    status = if (draft.isActive) "PUBLISHED" else "DRAFT",
                    attachmentFileIds = draft.attachmentFileIds
                    ,classTargets = draft.classTargetIds.takeIf { audienceCode == "CLASS" }
                ),
                currentUserId
            ).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage()
                    loadStatistics()
                },
                onFailure = { throwable -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to create event.") } }
            )
        }
    }

    private fun updateEvent(draft: EventDraft) {
        val eventId = draft.eventId?.trim().orEmpty()
        val eventDate = parseDisplayDate(draft.date)
        val minimumEventDate = currentAppDateLabel()
        val validationError = when {
            eventId.isBlank() -> "A valid event ID is required."
            draft.title.trim().isBlank() -> "Enter an event title."
            draft.description.trim().isBlank() -> "Enter event details."
            eventDate == null -> "Enter a valid date in DD MMM YYYY format."
            isEventDateBefore(draft.date, minimumEventDate) -> "Event date cannot be earlier than today."
            else -> null
        }
        if (validationError != null) {
            _uiState.update { it.copy(saveErrorMessage = validationError) }
            return
        }
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val audienceCode = resolveAudienceCode(draft.audience).getOrElse { throwable ->
                _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Event audience options are unavailable.") }
                return@launch
            }
            val preservedStatus = draft.existingStatus
                ?.trim()
                ?.uppercase()
                ?.takeIf { it in setOf("DRAFT", "PUBLISHED", "EXPIRED") }
            val preservedIsActive = when (preservedStatus) {
                "DRAFT", "EXPIRED" -> false
                "PUBLISHED" -> true
                else -> draft.isActive
            }
            EventsNewsRepository.updateEvent(
                eventId,
                EventUpdateRequest(
                    title = draft.title,
                    description = draft.description,
                    eventDate = "${eventDate}T23:59:59Z",
                    audience = audienceCode,
                    isActive = preservedIsActive,
                    status = preservedStatus ?: if (preservedIsActive) "PUBLISHED" else "DRAFT",
                    classTargets = draft.classTargetIds.takeIf { audienceCode == "CLASS" }
                ),
                currentUserId
            ).fold(
                onSuccess = {
                    draft.removedAttachmentFileIds.forEach { fileId ->
                        FileUploadRepository.delete(fileId).onFailure { throwable ->
                            scLog("[DATA_RETRIEVE_SC] EventsNewsViewModel remove attachment failure eventId=$eventId fileId=$fileId", throwable)
                        }
                    }
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage()
                    loadStatistics()
                },
                onFailure = { throwable -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to update event.") } }
            )
        }
    }

    private suspend fun resolveAudienceCode(value: String): Result<String> {
        val normalized = value.trim()
        if (normalized.isBlank()) return Result.failure(IllegalStateException("An event audience is required."))
        val items = SystemLookupRepository.loadCategoryItems("EVENT_AUDIENCE").getOrElse { return Result.failure(it) }
        val match = items.firstOrNull { item ->
            item.code.equals(normalized, ignoreCase = true) ||
                item.displayValue.equals(normalized, ignoreCase = true)
        }
        return match?.code?.trim()?.takeIf { it.isNotBlank() }?.let(Result.Companion::success)
            ?: Result.failure(IllegalStateException("Server value '$normalized' was not found in lookup category 'EVENT_AUDIENCE'."))
    }

    private fun deleteEvent(eventId: String) {
        val normalizedId = eventId.trim()
        if (normalizedId.isBlank()) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            EventsNewsRepository.deleteEvent(normalizedId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage()
                    loadStatistics()
                },
                onFailure = { throwable -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to delete event.") } }
            )
        }
    }

    private fun loadPage(debounce: Boolean = false) {
        listJob?.cancel()
        listJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            EventsNewsRepository.loadPage(
                // The list endpoint's q parameter supports both title and partial/full
                // event-id matching. Using event_id for every EVT- prefix made a partial
                // search such as EVT-10 behave like an exact lookup.
                query = state.searchQuery.trim().takeIf { it.isNotBlank() },
                eventId = null,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                status = state.filters.status.trim().uppercase().takeIf { it.isNotBlank() },
                audience = state.filters.eventType.trim().takeIf { it.isNotBlank() },
                publishedDate = state.filters.publishedDate.takeIf { it.isNotBlank() && !it.equals("All", true) && !it.equals("Custom Range", true) },
                publishedDateFrom = appDateInputToApi(state.filters.publishedDateFrom),
                publishedDateTo = appDateInputToApi(state.filters.publishedDateTo),
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc",
                refreshScope = scope
            ).fold(
                onSuccess = { result -> _uiState.update { it.copy(rows = result.items.map(EventResponse::toUi), totalCount = result.totalCount, isLoading = false, errorMessage = "") } },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] EventsNewsViewModel loadPage failure", throwable)
                    _uiState.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load events." }) }
                }
            )
        }
    }

    private fun loadStatistics() {
        statisticsJob?.cancel()
        statisticsJob = scope.launch {
            _uiState.update { it.copy(isStatisticsLoading = true) }
            EventsNewsRepository.loadStatistics().fold(
                onSuccess = { response ->
                    val c = response.counters
                    _uiState.update { it.copy(totalEvents = c.counter("total", "total_events") ?: 0, draftEvents = c.counter("draft", "draft_events") ?: 0, publishedEvents = c.counter("upcoming", "published", "published_events") ?: 0, expiredEvents = c.counter("past", "expired", "expired_events") ?: 0, isStatisticsLoading = false) }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] EventsNewsViewModel loadStatistics failure", throwable)
                    _uiState.update { it.copy(isStatisticsLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load event statistics." }) }
                }
            )
        }
    }

    fun clear() { listJob?.cancel(); statisticsJob?.cancel(); saveJob?.cancel(); viewJob?.cancel(); scope.cancel() }
}

private fun formatEventFileSize(bytes: Long): String = when {
    bytes >= 1024L * 1024L -> "${bytes / (1024L * 1024L)} MB"
    bytes >= 1024L -> "${bytes / 1024L} KB"
    else -> "$bytes B"
}

private fun parseDisplayDate(value: String): String? {
    val parts = value.trim().split(Regex("\\s+"))
    if (parts.size != 3) return null
    val day = parts[0].toIntOrNull()?.takeIf { it in 1..31 } ?: return null
    val month = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC").indexOf(parts[1].uppercase()).takeIf { it >= 0 }?.plus(1) ?: return null
    val year = parts[2].toIntOrNull()?.takeIf { it in 1900..2200 } ?: return null
    return year.toString().padStart(4, '0') + "-" + month.toString().padStart(2, '0') + "-" + day.toString().padStart(2, '0')
}

private fun isEventDateBefore(value: String, minimumDate: String): Boolean =
    appDateInputComparable(value)?.let { valueComparable ->
        appDateInputComparable(minimumDate)?.let { minimumComparable -> valueComparable < minimumComparable }
    } == true

private fun EventsSortField.toApiSortField(): String = when (this) {
    EventsSortField.ID -> "event_id"
    EventsSortField.TITLE -> "title"
    EventsSortField.AUDIENCE -> "audience"
    EventsSortField.PUBLISHED_DATE, EventsSortField.EXPIRY_DATE -> "event_date"
    EventsSortField.STATUS -> "status"
}

private fun Map<String, Int>.counter(vararg names: String): Int? = names.firstNotNullOfOrNull { this[it] ?: this[it.lowercase()] ?: this[it.uppercase()] }

private fun EventResponse.toUi() = EventRowUi(
    id = eventId.orEmpty().ifBlank { id.orEmpty().ifBlank { "—" } },
    backendId = id.orEmpty().ifBlank { eventId.orEmpty() },
    title = title.orEmpty().ifBlank { "Untitled event" },
    eventType = audience.orEmpty().ifBlank { "Other" },
    publishedOn = createdAt.toDisplayDate(),
    expiryDate = eventDate.toDisplayDate(),
    status = when {
        !status.isNullOrBlank() -> status.toDisplayStatus()
        isActive == false -> "Draft"
        !eventDate.isNullOrBlank() && eventDate < Clock.System.now().toString() -> "Expired"
        else -> "Published"
    },
    description = description.orEmpty(),
    audience = audience.orEmpty().ifBlank { "All Parents" },
    imageUrl = attachmentFileUrls?.firstOrNull().orEmpty(),
    attachmentFileIds = attachmentFileIds.orEmpty(),
    attachmentFileUrls = attachmentFileUrls.orEmpty()
)

private fun String.toDisplayStatus(): String = when (trim().uppercase()) {
    "DRAFT" -> "Draft"
    "PUBLISHED" -> "Published"
    "EXPIRED" -> "Expired"
    else -> trim().replace('_', ' ').lowercase().replaceFirstChar { it.uppercase() }
}

private fun String?.toDisplayDate(): String {
    val parts = this.orEmpty().substringBefore('T').split('-')
    if (parts.size != 3) return this.orEmpty().ifBlank { "—" }
    val month = parts[1].toIntOrNull() ?: return this.orEmpty()
    val label = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC").getOrNull(month - 1) ?: return this.orEmpty()
    return "${parts[2].padStart(2, '0')} $label ${parts[0]}"
}
